import authImage1 from '@assets/auth/1.webp';
import authImage2 from '@assets/auth/2.webp';
import authImage3 from '@assets/auth/3.webp';
import authImage4 from '@assets/auth/4.webp';
import authImage5 from '@assets/auth/5.webp';
import authImage6 from '@assets/auth/6.webp';
import authImage7 from '@assets/auth/7.webp';
import authImage8 from '@assets/auth/8.webp';
import authImage9 from '@assets/auth/9.webp';
import authImage10 from '@assets/auth/10.webp';
import { NEW_PASSWORD_FIELD_NAME, REPEAT_PASSWORD_FIELD_NAME, TOKEN_CLAIMS } from '@constants/auth.ts';
import { PASSWORD_REQUIREMENTS } from '@constants/passwordRequirements.ts';
import type { PasswordPolicyResponse } from '@shared/api/clientStatus.api.ts';
import { readToken } from '@store/services/localStorage.service.ts';
import type { FormField } from '@types';
import { isEqualIgnoreCase } from '@utils/text.ts';
import { jwtDecode } from 'jwt-decode';

interface FieldErrorInput {
    name: string;
    message: string;
}

// helpers got show hint for policy
const getFirstUnfulfilledPolicy = (
    policies: PasswordPolicyResponse[],
    password: string,
): PasswordPolicyResponse | null => {
    for (const policy of policies) {
        // Skip policies without a regex or with a disabled requirement
        if (!policy.regexPattern || policy.passwordRequirementValue === 0) continue;
        const re = new RegExp(policy.regexPattern);
        if (!re.test(password)) {
            return policy;
        }
    }
    return null;
};

export const getPasswordPolicyHint = (policies: PasswordPolicyResponse[], password: string): string => {
    const firstMissing = getFirstUnfulfilledPolicy(policies, password);
    if (firstMissing && firstMissing.description) {
        return firstMissing.description;
    }
    return '';
};

export const getPasswordDefaultHint = (policies: PasswordPolicyResponse[]): string => {
    const valueByRequirementType = new Map<string, number>(
        policies.map((policy) => [policy.passwordRequirementType, policy.passwordRequirementValue]),
    );

    const minLength = valueByRequirementType.get(PASSWORD_REQUIREMENTS.MIN_LENGTH);
    const maxLength = valueByRequirementType.get(PASSWORD_REQUIREMENTS.MAX_LENGTH);

    const hintSegments: string[] = [`${minLength}-${maxLength} chars`];
    const compositionParts: string[] = [];

    if ((valueByRequirementType.get(PASSWORD_REQUIREMENTS.MIN_UPPERCASE) || 0) > 0) {
        compositionParts.push('uppercase');
    }
    if ((valueByRequirementType.get(PASSWORD_REQUIREMENTS.MIN_LOWERCASE) || 0) > 0) {
        compositionParts.push('lowercase');
    }
    if ((valueByRequirementType.get(PASSWORD_REQUIREMENTS.MIN_DIGIT) || 0) > 0) {
        compositionParts.push('number');
    }
    if ((valueByRequirementType.get(PASSWORD_REQUIREMENTS.MIN_SPECIALCHARACTER) || 0) > 0) {
        compositionParts.push('symbol');
    }

    if (compositionParts.length > 0) {
        hintSegments.push(`must include ${compositionParts.join(', ')}`);
    }

    return hintSegments.join(', ');
};

export const validatePasswordChange = ({
    newPassword,
    repeatPassword,
    getNextPolicyHint,
    policies,
}: {
    newPassword: string;
    repeatPassword: string;
    getNextPolicyHint: (policies: PasswordPolicyResponse[], password: string) => string;
    policies?: PasswordPolicyResponse[];
}): { canProceed: boolean; hint: string } => {
    if (policies && policies.length > 0) {
        const policyHint = getNextPolicyHint(policies, newPassword);
        if (policyHint) {
            return { canProceed: false, hint: policyHint }; // Policy not fulfilled
        }
    }

    if (newPassword !== repeatPassword) {
        return { canProceed: false, hint: '' }; // Passwords don't match
    }
    return { canProceed: true, hint: '' }; // All good
};

export const getRandomAuthImage = (): string => {
    const images = [
        authImage1,
        authImage2,
        authImage3,
        authImage4,
        authImage5,
        authImage6,
        authImage7,
        authImage8,
        authImage9,
        authImage10,
    ];
    const randomIndex = Math.floor(Math.random() * images.length);

    return images[randomIndex];
};

export const isFieldErroneous = (fieldName: string, fields: FormField[]): boolean =>
    fields.some((field) => field.name?.[0] === fieldName && Array.isArray(field.errors) && field.errors.length > 0);

export const getFieldError = (fieldName: string, fields: FormField[]): string =>
    isFieldErroneous(fieldName, fields) ? fields.filter((f) => f.name[0] === fieldName)[0].errors[0] : '';

export const getUpdatedFormFieldsWithErrors = (
    currentFields: FormField[],
    errors: FieldErrorInput[],
): { updatedFields: FormField[]; fieldsHaveErrors: boolean } => {
    const updatedFields = [...currentFields];

    errors.forEach(({ name, message }) => {
        const index = updatedFields.findIndex((f) => isEqualIgnoreCase(f.name[0], name));

        if (index !== -1) {
            updatedFields[index] = {
                ...updatedFields[index],
                errors: [message],
            };
        } else {
            updatedFields.push({
                name: [name],
                touched: true,
                validating: false,
                validated: true,
                errors: [message],
                warnings: [],
            });
        }
    });

    const fieldsHaveErrors = updatedFields.some((f) => f.errors.length > 0);

    return { updatedFields, fieldsHaveErrors };
};

export const getFormFieldValue = (fieldName: string, fields: FormField[]): unknown => {
    const field = fields.find((f) => f.name[0] === fieldName);

    return field?.value;
};

export const arePasswordsMismatched = (formFields: FormField[], newPasswordFieldName = NEW_PASSWORD_FIELD_NAME) =>
    getFormFieldValue(newPasswordFieldName, formFields) !== getFormFieldValue(REPEAT_PASSWORD_FIELD_NAME, formFields);

const readTokenPayload = (): Record<string, any> | null => {
    const token = readToken();
    if (!token) return null;

    try {
        return jwtDecode<Record<string, any>>(token);
    } catch {
        return null;
    }
};

export const getUserRoles = (): string[] | null => {
    const payload = readTokenPayload();
    if (!payload) return null;

    const roleKey = Object.keys(payload).find((k) => k.toLowerCase().endsWith('/role'));

    return roleKey ? payload[roleKey] : null;
};

export const getAdminCenterContextFromToken = (): { clientId: number | null; tenantAlias: string | null } => {
    const payload = readTokenPayload();
    if (!payload) return { clientId: null, tenantAlias: null };

    const clientId = Number(payload[TOKEN_CLAIMS.CLIENT_ID]);
    const tenantAlias = payload[TOKEN_CLAIMS.TENANT];

    return {
        clientId: Number.isInteger(clientId) && clientId > 0 ? clientId : null,
        tenantAlias: typeof tenantAlias === 'string' && tenantAlias.length > 0 ? tenantAlias : null,
    };
};

export const showErrorForNewPasswordField = (
    formFields: FormField[],
    setFormFields: (updatedFields: FormField[]) => void,
    errorMessage: string,
    fieldName = NEW_PASSWORD_FIELD_NAME,
) => {
    const { updatedFields } = getUpdatedFormFieldsWithErrors(formFields, [{ name: fieldName, message: errorMessage }]);
    setFormFields(updatedFields);
};

export const isTokenValid = (token: string): boolean => {
    if (!token) return false;

    try {
        const decoded = jwtDecode(token);

        if (!decoded.exp) return false;

        const now = Math.floor(Date.now() / 1000);

        return decoded.exp > now;
    } catch (err) {
        console.error('Invalid token', err);

        return false;
    }
};
